/**
 * P&L Monthly tab (A7Y-2054): month-over-month table, newest-first (the API
 * already returns it that way). Each row links to the month-detail page.
 * Presentational — takes the `monthly` series.
 */
import { cn, formatCurrency } from "@/lib/utils";
import type { PnlMonthSummary } from "@a7y/types";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { formatMonthLabel } from "../format";

function netClass(net: number): string {
  return net > 0
    ? "text-emerald-600 dark:text-emerald-400"
    : net < 0
      ? "text-rose-600 dark:text-rose-400"
      : "text-zinc-500 dark:text-zinc-400";
}

function signedCurrency(net: number): string {
  return `${net < 0 ? "−" : ""}${formatCurrency(Math.abs(net))}`;
}

export function MonthlyTab({ monthly }: { monthly: PnlMonthSummary[] }) {
  if (monthly.length === 0) {
    return (
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-10 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 text-center">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">No months to show yet.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white dark:bg-zinc-800 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
              <th className="px-6 py-3">Month</th>
              <th className="px-6 py-3 text-right">Spend</th>
              <th className="px-6 py-3 text-right">Revenue</th>
              <th className="px-6 py-3 text-right">Net</th>
              <th className="px-6 py-3 w-8" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {monthly.map((m) => (
              <tr
                key={m.month}
                className="group border-b border-zinc-100 dark:border-zinc-700/60 last:border-0 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-700/30"
              >
                <td className="px-6 py-0">
                  <Link
                    to={`/admin/finance/pnl/${m.month}`}
                    className="flex items-center py-3.5 font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 focus:outline-none focus-visible:underline"
                  >
                    {formatMonthLabel(m.month)}
                  </Link>
                </td>
                <td className="px-6 py-3.5 text-right tabular-nums text-zinc-700 dark:text-zinc-300">
                  {formatCurrency(m.spend)}
                </td>
                <td className="px-6 py-3.5 text-right tabular-nums text-zinc-700 dark:text-zinc-300">
                  {formatCurrency(m.revenue)}
                </td>
                <td className={cn("px-6 py-3.5 text-right tabular-nums font-medium", netClass(m.net))}>
                  {signedCurrency(m.net)}
                </td>
                <td className="px-6 py-3.5 text-right">
                  <ChevronRight className="inline h-4 w-4 text-zinc-300 dark:text-zinc-600 group-hover:text-blue-500 transition-colors" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
